import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'authority-labs-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss']
})
export class PdfViewerComponent implements AfterViewInit {
  @Input() pdfUrl?: string;
  @Input() widthMm?: number;
  @Input() pages?: string = '';
  @Output() loaded: EventEmitter<void> = new EventEmitter();

  @ViewChild('pages', { static: true }) pagesRef!: ElementRef<HTMLDivElement>;

  isLoaded$ = new BehaviorSubject(false);

  private readonly CORE_PATH = `./assets/pdfjs/legacy/build/pdf.mjs`;
  private readonly WORKER_PATH = `./assets/pdfjs/legacy/build/pdf.worker.mjs`;
  private readonly CMAP_PATH = `./assets/pdfjs/cmaps/`;
  private readonly FONTS_PATH = `./assets/pdfjs/standard_fonts/`;

  // Constants used for layout/scaling
  private readonly CSS_PX_PER_MM = 96 / 25.4; // CSS inch = 96px
  private readonly MAX_CSS_HEIGHT_PX = 700; // Limit visible CSS height per page

  constructor() {}

  async ngAfterViewInit(): Promise<void> {
    try {
      const targetWidthMm = this.clampWidthMm(
        this.widthMm ??
          (isFinite(this.widthMm) && this.widthMm > 0 ? this.widthMm : 180)
      );

      if (!this.pdfUrl) {
        console.error('PDF render error: missing pdfUrl');
        return;
      }

      const coreUrl = this.resolveAssetUrl(this.CORE_PATH);
      const pdfjsLib: any = await import(/* webpackIgnore: true */ coreUrl);
      (pdfjsLib as any).GlobalWorkerOptions.workerSrc = this.resolveAssetUrl(
        this.WORKER_PATH
      );

      // Device pixel ratio for crisp rendering
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 2));

      // Start loading the PDF
      const loadingTask = pdfjsLib.getDocument({
        url: this.pdfUrl,
        cMapUrl: this.resolveAssetUrl(this.CMAP_PATH),
        cMapPacked: true,
        standardFontDataUrl: this.resolveAssetUrl(this.FONTS_PATH),
        disableFontFace: false
      });
      const pdf = await loadingTask.promise;

      const container = this.pagesRef.nativeElement;

      for (let p = 1; p <= pdf.numPages; p++) {
        if (!this.inRange(p, this.pages) && !!this.pages) {
          continue;
        }
        const page = await pdf.getPage(p);

        // Compute scale so CSS width equals the requested mm width
        const baseVp = page.getViewport({ scale: 1 });
        const targetCssWidthPx = targetWidthMm * this.CSS_PX_PER_MM;
        const scaleForWidth = targetCssWidthPx / baseVp.width;
        const scaleForHeight = this.MAX_CSS_HEIGHT_PX / baseVp.height;
        const scaleForCss = Math.min(scaleForWidth, scaleForHeight);

        // Render at device pixels for crispness
        const deviceVp = page.getViewport({ scale: scaleForCss * dpr });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { alpha: false });

        canvas.width = Math.round(deviceVp.width);
        canvas.height = Math.round(deviceVp.height);

        // Set CSS size (visual size in layout)
        const cssWidthPx = baseVp.width * scaleForCss;
        const cssHeightPx = baseVp.height * scaleForCss;
        canvas.style.width = Math.round(cssWidthPx) + 'px';
        canvas.style.height = Math.round(cssHeightPx) + 'px';

        canvas.classList.add('canvas');
        container.appendChild(canvas);

        await page.render({ canvasContext: ctx, viewport: deviceVp }).promise;
      }

      this.isLoaded$.next(true);
    } catch (err) {
      console.error('PDF render error:', err);
    }

    this.loaded.emit();
  }

  // Ensure width stays within 10–180 mm like the original logic
  private clampWidthMm(mm: number): number {
    if (!isFinite(mm)) return 180;
    return Math.max(10, Math.min(180, mm));
  }

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private inRange(x: number, range: string = ''): boolean {
    const parts = range.split(',').map((part) => part.trim());

    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number);
        if (x >= start && x <= end) return true;
      } else if (Number(part) === x) {
        return true;
      }
    }

    return false;
  }
}
